import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toAgentAnswer } from '@/components/agent/agentAnswer';
import { runAgentChatWithPolling, type AgentJobPollResult } from '@/services/agentChatJobPoller';
import type { AgentChatMessage } from '@/types/agent.types';
import { logoutUser } from '@/store/slices/authSlice';
import { mapAgentChatSendError } from '@/store/slices/agentChatSliceErrors';

function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

interface AgentChatState {
  messages: AgentChatMessage[];
  /** Primera petición sin id; el BFF/App-Core devuelve `session_id` en el 202 y se reutiliza en turnos siguientes. */
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AgentChatState = {
  messages: [],
  currentSessionId: null,
  isLoading: false,
  error: null,
};

export interface SendAgentChatArg {
  question: string;
}

function fallbackContentFor(result: AgentJobPollResult): string {
  return toAgentAnswer(result.payload).insight_text || 'Response generated.';
}

export const sendAgentChatMessage = createAsyncThunk<
  AgentJobPollResult,
  SendAgentChatArg,
  { rejectValue: string }
>(
  'agentChat/send',
  async ({ question }, { rejectWithValue, signal, getState }) => {
    const trimmed = question.trim();
    if (!trimmed) {
      return rejectWithValue('');
    }

    try {
      const sessionId = (getState() as { agentChat: AgentChatState }).agentChat.currentSessionId;
      return await runAgentChatWithPolling(trimmed, signal, sessionId);
    } catch (err: unknown) {
      if (signal.aborted) {
        return rejectWithValue('');
      }
      return rejectWithValue(mapAgentChatSendError(err));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { agentChat: AgentChatState };
      return !state.agentChat.isLoading;
    },
  }
);

const agentChatSlice = createSlice({
  name: 'agentChat',
  initialState,
  reducers: {
    clearAgentChatError: state => {
      state.error = null;
    },
    clearAgentChat: () => initialState,
    setSessionId: (state, action: { payload: string | null }) => {
      state.currentSessionId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sendAgentChatMessage.pending, (state, action) => {
        const trimmed = action.meta.arg.question.trim();
        if (!trimmed) return;
        state.isLoading = true;
        state.error = null;
        state.messages.push({
          id: newMessageId(),
          role: 'user',
          content: trimmed,
        });
      })
      .addCase(sendAgentChatMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSessionId = action.payload.sessionId;
        const result = action.payload;
        state.messages.push({
          id: newMessageId(),
          role: 'agent',
          content: fallbackContentFor(result),
          agentData: result.payload,
        });
      })
      .addCase(sendAgentChatMessage.rejected, (state, action) => {
        state.isLoading = false;
        const msg = action.payload;
        if (msg === undefined || msg === '') return;
        state.error = msg;
      })
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { clearAgentChatError, clearAgentChat, setSessionId } = agentChatSlice.actions;
export default agentChatSlice.reducer;
